import { convertRGBAArrayToHexString } from "@nova/util-strings"
import {
    selectInfraredFlightById,
    selectShowLayerByIdMapForVisibleHotspotPoints,
    selectShowLayerByIdMapForVisibleHotspotPolygons,
} from "features/entity-caching/flights/selectors"
import { selectFilteredHotspotsByFlightId } from "features/entity-caching/hotspots/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { ReactElement, useMemo } from "react"
import { LegendItem, LegendItemProps } from "./useLegendItems"
import * as Icons from "@nova/component-icons"
import {
    AreaUnit,
    ColorSwatchCategory,
    hexColorSwatches,
    HotspotPointTypes,
    HotspotStatus,
    HotspotWithContext,
} from "@nova/constants"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { getHotspotFillColorAndLabel } from "@nova/util-hotspot"
import {
    selectLegendPosition,
    selectPDFLegendSize,
    selectVisibleStatItems,
} from "plugins/PDFv2/PDFSlice"
import { calculateArea } from "@nova/calculate-geospatial"
import { formatArea } from "@nova/format"
import { config } from "@nova/configuration/browser"
import { isLegendItemInPDFSnapshot } from "./module"
import { useViewport } from "features/map/GlobalViewState"

const defaultPolygonFill = hexColorSwatches[ColorSwatchCategory.PolygonFill][0]
const defaultPolygonOutline = hexColorSwatches[ColorSwatchCategory.PolygonOutline][0]

export const useHotspotLegendItems = ({ offsetX, offsetY, width, height }: LegendItemProps) => {
    const showHotspotPointsLayerByFlightId = useAppSelector(
        selectShowLayerByIdMapForVisibleHotspotPoints
    )
    const showHotspotPolygonLayerByFlightId = useAppSelector(
        selectShowLayerByIdMapForVisibleHotspotPolygons
    )
    const visibleStats = useAppSelector(selectVisibleStatItems())
    const legendSize = useAppSelector(selectPDFLegendSize())
    const legendPosition = useAppSelector(selectLegendPosition())
    const view = useViewport()

    const { organizationSettingArea } = config.use("organizationSettingArea") as {
        organizationSettingArea: AreaUnit
    }

    const hotspots = useMemo(() => {
        const hotspotData: (HotspotWithContext & {
            color: [number, number, number, number]
            label: string
            hotspotType: HotspotPointTypes
        })[] = []
        const state = store.getState()
        Array.from(
            new Set(
                [
                    ...Object.keys(showHotspotPointsLayerByFlightId),
                    ...Object.keys(showHotspotPolygonLayerByFlightId),
                ].filter(
                    (key) =>
                        showHotspotPointsLayerByFlightId[key] ||
                        showHotspotPolygonLayerByFlightId[key]
                )
            )
        ).forEach((flightId) => {
            const hotspots = selectFilteredHotspotsByFlightId(flightId)(state)
            const flight = selectInfraredFlightById(flightId)(state)
            hotspots.forEach((hotspot) => {
                if (hotspot.status === HotspotStatus.Removed) return
                const distance = hotspot.distanceToPerimeter
                if (
                    flight?.perimeterDistance?.range.at(-1)?.distance &&
                    (distance || 0) > (flight.perimeterDistance?.range.at(-1)?.distance as number)
                )
                    return

                const [label, color] = getHotspotFillColorAndLabel(hotspot, flight)
                hotspotData.push({
                    ...hotspot,
                    color,
                    label,
                    hotspotType: flight?.pointType || HotspotPointTypes.Point,
                })
            })
        })
        return hotspotData
    }, [showHotspotPointsLayerByFlightId, showHotspotPolygonLayerByFlightId])

    const [uniqueHotspotColorsInView, uniquePolygonColoursInView, counts, areas] = useMemo(() => {
        const pointColours = new Set<string>()
        const polygonColours = new Set<string>()
        const state = store.getState()
        const counts: Record<string, number> = {}
        const areas: Record<string, number> = {}
        hotspots.forEach((hotspot) => {
            const [x, y] = view.project([hotspot.longitude, hotspot.latitude])
            const isInsideSnapshot = isLegendItemInPDFSnapshot(
                x,
                y,
                {
                    offsetX,
                    offsetY,
                    width,
                    height,
                },
                legendSize,
                legendPosition
            )
            if (isInsideSnapshot) {
                const flight = selectInfraredFlightById(hotspot.flightId)(state)
                if (showHotspotPointsLayerByFlightId[hotspot.flightId]) {
                    const key =
                        hotspot.label +
                        ":" +
                        convertRGBAArrayToHexString(hotspot.color) +
                        ":" +
                        hotspot.hotspotType
                    pointColours.add(key)
                    counts[key] = (counts[key] || 0) + 1
                }
                if (showHotspotPolygonLayerByFlightId[hotspot.flightId]) {
                    const key =
                        (flight?.polyFill
                            ? decodeNumberToColor(flight.polyFill)
                            : defaultPolygonFill) +
                        ":" +
                        (flight?.polyStroke || defaultPolygonOutline)
                    polygonColours.add(key)
                    const area = hotspot.polygon.length > 2 ? calculateArea(hotspot.polygon) : 0
                    areas[key] = (areas[key] || 0) + area
                }
            }
        })
        return [pointColours, polygonColours, counts, areas]
    }, [
        hotspots,
        view,
        showHotspotPointsLayerByFlightId,
        height,
        width,
        offsetX,
        offsetY,
        showHotspotPolygonLayerByFlightId,
        legendPosition,
        legendSize,
    ])

    const tryGetDistance = (str: string) => {
        if (str.includes("<")) return parseInt(str.split(" ")[2].replace(",", ""))
        else return 0
    }

    const hotspotLegendItems = useMemo(() => {
        const legendItems: LegendItem[] = []
        Array.from(uniqueHotspotColorsInView)
            .sort((a, b) => tryGetDistance(a) - tryGetDistance(b))
            .forEach((data) => {
                const [label, color, hotspotType] = data.split(":")

                let icon: ReactElement
                switch (hotspotType) {
                    case "2":
                        icon = <Icons.HotspotHeatDot />
                        break
                    default:
                        icon = <Icons.Hotspot className="m-auto w-[16px]" style={{ fill: color }} />
                        break
                }

                const id = "hotspot" + label + color // Add color to ID to separate hotspot colors in the legend.
                const showQuantity = visibleStats[id] ?? true

                const title = showQuantity ? `${label} (${counts[data] || 0})` : label

                legendItems.push({
                    icon: icon,
                    title,
                    id: id,
                    hotspotType: hotspotType as "point" | "heat" | "hex",
                    stat: { label: "Quantity", stat: "", isSetByDefault: true }, // hotspots are special case where the stat shows in the label, and are enabled by default
                })
            })
        Array.from(uniquePolygonColoursInView).forEach((color) => {
            legendItems.push({
                icon: (
                    <Icons.Polygon
                        className="m-auto w-[16px]"
                        style={{
                            fill: color.split(":")[0],
                            stroke: color.split(":")[1],
                        }}
                    />
                ),
                title: "Hot Area",
                id: "hotarea" + color,
                stat: {
                    label: "Area",
                    stat: formatArea({ value: areas[color], unit: organizationSettingArea }),
                },
            })
        })
        return legendItems
    }, [
        uniqueHotspotColorsInView,
        uniquePolygonColoursInView,
        counts,
        areas,
        visibleStats,
        organizationSettingArea,
    ])
    return hotspotLegendItems
}
